
import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";

type ActivityLevel = 'sedentary' | 'light' | 'moderate' | 'active' | 'very-active';
type Gender = 'male' | 'female';
type Goal = 'maintain' | 'lose' | 'gain';

const CalorieCalculator = () => {
  const [age, setAge] = useState<string>('30');
  const [weight, setWeight] = useState<string>('70');
  const [height, setHeight] = useState<string>('170');
  const [gender, setGender] = useState<Gender>('male');
  const [activityLevel, setActivityLevel] = useState<ActivityLevel>('moderate');
  const [goal, setGoal] = useState<Goal>('maintain');
  const [calories, setCalories] = useState<number | null>(null);

  const calculateBMR = (): number => {
    const ageNum = parseInt(age);
    const weightNum = parseInt(weight);
    const heightNum = parseInt(height);

    // Mifflin-St Jeor Equation
    if (gender === 'male') {
      return 10 * weightNum + 6.25 * heightNum - 5 * ageNum + 5;
    } else {
      return 10 * weightNum + 6.25 * heightNum - 5 * ageNum - 161;
    }
  };

  const calculateTDEE = (bmr: number): number => {
    const activityMultipliers: Record<ActivityLevel, number> = {
      'sedentary': 1.2,
      'light': 1.375,
      'moderate': 1.55,
      'active': 1.725,
      'very-active': 1.9
    };
    
    return Math.round(bmr * activityMultipliers[activityLevel]);
  };

  const calculateCalories = (tdee: number): number => {
    const goalMultipliers: Record<Goal, number> = {
      'maintain': 1,
      'lose': 0.8,
      'gain': 1.15
    };
    
    return Math.round(tdee * goalMultipliers[goal]);
  };

  const handleCalculate = () => {
    try {
      if (!age || !weight || !height) {
        toast.error("Please fill in all fields");
        return;
      }
      
      const bmr = calculateBMR();
      const tdee = calculateTDEE(bmr);
      const goalCalories = calculateCalories(tdee);
      
      setCalories(goalCalories);
      toast.success("Calories calculated successfully");
    } catch (error) {
      toast.error("Error calculating calories");
      console.error(error);
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto">
      <Card>
        <CardHeader>
          <CardTitle>Calorie Calculator</CardTitle>
          <CardDescription>
            Calculate your daily calorie needs based on your age, gender, weight, height, and activity level.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-6">
              <div>
                <Label htmlFor="gender">Gender</Label>
                <RadioGroup
                  id="gender"
                  value={gender}
                  onValueChange={(value: string) => setGender(value as Gender)}
                  className="flex gap-4 mt-2"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="male" id="male" />
                    <Label htmlFor="male">Male</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="female" id="female" />
                    <Label htmlFor="female">Female</Label>
                  </div>
                </RadioGroup>
              </div>

              <div>
                <Label htmlFor="age">Age</Label>
                <Input
                  id="age"
                  type="number"
                  value={age}
                  onChange={(e) => setAge(e.target.value)}
                  placeholder="Age in years"
                  className="mt-1"
                  min="15"
                  max="100"
                />
              </div>

              <div>
                <Label htmlFor="height">Height (cm)</Label>
                <Input
                  id="height"
                  type="number"
                  value={height}
                  onChange={(e) => setHeight(e.target.value)}
                  placeholder="Height in centimeters"
                  className="mt-1"
                  min="100"
                  max="250"
                />
              </div>

              <div>
                <Label htmlFor="weight">Weight (kg)</Label>
                <Input
                  id="weight"
                  type="number"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  placeholder="Weight in kilograms"
                  className="mt-1"
                  min="30"
                  max="300"
                />
              </div>
            </div>

            <div className="space-y-6">
              <div>
                <Label htmlFor="activity">Activity Level</Label>
                <Select
                  value={activityLevel}
                  onValueChange={(value: string) => setActivityLevel(value as ActivityLevel)}
                >
                  <SelectTrigger id="activity" className="mt-1">
                    <SelectValue placeholder="Select activity level" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sedentary">Sedentary (little or no exercise)</SelectItem>
                    <SelectItem value="light">Lightly active (light exercise 1-3 days/week)</SelectItem>
                    <SelectItem value="moderate">Moderately active (moderate exercise 3-5 days/week)</SelectItem>
                    <SelectItem value="active">Active (hard exercise 6-7 days/week)</SelectItem>
                    <SelectItem value="very-active">Very active (very hard exercise & physical job)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="goal">Goal</Label>
                <Select 
                  value={goal}
                  onValueChange={(value: string) => setGoal(value as Goal)}
                >
                  <SelectTrigger id="goal" className="mt-1">
                    <SelectValue placeholder="Select your goal" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="maintain">Maintain weight</SelectItem>
                    <SelectItem value="lose">Lose weight</SelectItem>
                    <SelectItem value="gain">Gain weight</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button 
                onClick={handleCalculate} 
                className="w-full mt-6"
              >
                Calculate Calories
              </Button>
            </div>
          </div>

          {calories !== null && (
            <div className="mt-8 p-6 border rounded-lg bg-blue-50">
              <h3 className="text-xl font-semibold mb-2">Your Results</h3>
              <p className="text-lg">
                Recommended Daily Calories: <span className="font-bold text-primary">{calories} calories</span>
              </p>
              <p className="mt-4 text-sm text-muted-foreground">
                This calculation is based on the Mifflin-St Jeor equation, which is considered one of the most accurate formulas for estimating calorie needs.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CalorieCalculator;
